import React, { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, UserPlus, GripVertical } from "lucide-react";

// Multi-user Assign Team modal — super-admins can set 1+ owners on a role.
// The FIRST checked user is the primary owner (highlighted); the rest are
// team members with matching permission to act on the record.
export default function AssignOwnerDialog({ record, defaultRole = "production", onClose, onSaved }) {
  const [team, setTeam] = useState([]);
  const [role, setRole] = useState(defaultRole);
  const [selected, setSelected] = useState([]); // ordered list of user ids
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/onboarding/team")
      .then(({ data }) => setTeam(data || []))
      .catch(() => toast.error("Failed to load team"));
  }, []);

  // Preselect current team + primary for this role.
  useEffect(() => {
    const teamField = { production: "assigned_production_team", testing: "assigned_testing_team", install: "assigned_install_team" }[role];
    const primaryField = { production: "assigned_production_user_id", testing: "assigned_testing_user_id", install: "assigned_install_user_id" }[role];
    const currTeam = (record?.[teamField] || []).map((u) => u.id);
    const primary = record?.[primaryField];
    // Ensure primary is first if present.
    let next = currTeam.slice();
    if (primary && next.includes(primary)) {
      next = [primary, ...next.filter((x) => x !== primary)];
    } else if (primary) {
      next = [primary, ...next];
    }
    setSelected(next);
  }, [role, record]);

  const eligible = team.filter((u) => role === "production" ? u.can_production : role === "testing" ? u.can_testing : u.can_install);

  const toggle = (uid) => {
    setSelected((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };
  const makePrimary = (uid) => {
    setSelected((prev) => [uid, ...prev.filter((x) => x !== uid)]);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/onboarding/${record.id}/assign-team`, {
        role, user_ids: selected,
      });
      toast.success(selected.length === 0 ? "Assignment cleared" : selected.length === 1 ? "Owner assigned" : `${selected.length} owners assigned`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save assignment");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="assign-owner-dialog" onClick={onClose}>
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[#E9ECEF] flex items-center gap-3">
          <UserPlus size={16} className="text-[#D4AF37]" />
          <div className="flex-1">
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#D4AF37]">Assign Team</div>
            <h3 className="font-display text-base font-black text-[#0B132B] leading-tight">{record.dealer_name}</h3>
          </div>
          <button onClick={onClose} data-testid="assign-owner-close" className="text-[#6C757D] hover:text-[#0B132B]"><X size={16} /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          <div className="mb-3">
            <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="assign-owner-role"
                    className="w-full mt-1 text-sm rounded-sm border border-[#E9ECEF] bg-white px-3 py-2">
              <option value="production">Production team</option>
              <option value="testing">Testing team</option>
              <option value="install">Install team</option>
            </select>
          </div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Members ({selected.length})</label>
            <div className="text-[10px] text-[#6C757D] italic">First = primary owner</div>
          </div>
          {eligible.length === 0 ? (
            <div className="text-[11px] text-amber-700 py-3">No users currently hold the <b>{role}</b> permission. Grant it in Admin → Users.</div>
          ) : (
            <div className="border border-[#E9ECEF] rounded-sm divide-y divide-[#F1F3F5] max-h-[300px] overflow-y-auto">
              {eligible.map((u) => {
                const idx = selected.indexOf(u.id);
                const isSelected = idx >= 0;
                const isPrimary = idx === 0;
                return (
                  <label key={u.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F8F9FA] ${isPrimary ? "bg-[#D4AF37]/10" : ""}`} data-testid={`assign-owner-option-${u.id}`}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggle(u.id)} data-testid={`assign-owner-check-${u.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-[#0B132B] truncate">{u.name}</div>
                      <div className="text-[10px] text-[#6C757D] truncate">{u.email} · {u.role}{u.agency ? ` · ${u.agency}` : ""}</div>
                    </div>
                    {isSelected && !isPrimary && (
                      <button type="button" onClick={(e) => { e.preventDefault(); makePrimary(u.id); }}
                              className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#D4AF37] hover:text-[#0B132B]"
                              data-testid={`assign-owner-primary-${u.id}`}>
                        Make primary
                      </button>
                    )}
                    {isPrimary && <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#0B132B]"><GripVertical size={10} className="inline"/> Primary</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#E9ECEF] flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="assign-owner-cancel"
                  className="rounded-sm text-[11px] uppercase tracking-[0.14em] font-bold">Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="assign-owner-save"
                  className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white text-[11px] uppercase tracking-[0.14em] font-bold">
            {selected.length === 0 ? "Clear assignment" : `Save ${selected.length} member${selected.length !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
